import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import TagIcon from "@mui/icons-material/Tag";
import axios from "axios";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import apiEndpoints from "../../../apiconfig";
import { apiErrorMessage } from "./apiErrorMessage";

// Receipt numbers come from a counter that never goes backwards, so deleting
// payments doesn't free their numbers. Reset (e.g. after clearing test data)
// continues from the highest receipt number still in the transactions table.
export default function ReceiptNumberSettings({ glass }) {
  const token = localStorage.getItem("token");
  const { showSnackbar } = useSnackbar();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const res = await axios.get(`${apiEndpoints.settings}?action=get_receipt_counter`, authHeaders);
      if (typeof res.data === "string") throw new Error("NOT_DEPLOYED");
      if (res.data?.status !== "success") throw new Error(res.data?.message);
      setInfo(res.data.data);
      setLoadError("");
    } catch (err) {
      setLoadError(
        err?.message === "Invalid action"
          ? "Server API is out of date: upload the latest api/settings.php."
          : apiErrorMessage(err, "settings.php", "Failed to load receipt numbering"),
      );
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = async () => {
    setConfirmOpen(false);
    setResetting(true);
    try {
      const res = await axios.post(`${apiEndpoints.settings}?action=reset_receipt_counter`, {}, authHeaders);
      if (typeof res.data === "string") throw new Error("NOT_DEPLOYED");
      if (res.data?.status !== "success") throw new Error(res.data?.message);
      setInfo(res.data.data);
      showSnackbar(res.data.message, "success");
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "settings.php", "Failed to reset receipt numbering"), "error");
    } finally {
      setResetting(false);
    }
  };

  const canReset = info && info.next_receipt_no > info.highest_receipt_no + 1;

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <TagIcon sx={{ color: "#3F51B5" }} />
        <Typography variant="h6" fontWeight={700}>
          Receipt Numbers
        </Typography>
      </Stack>

      <Paper sx={{ ...glass, p: { xs: 2, sm: 3 } }}>
        {loadError ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {loadError}
          </Alert>
        ) : !info ? (
          <CircularProgress size={22} />
        ) : (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3} mb={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Next receipt number
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {info.next_receipt_no}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Highest receipt in payments
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {info.highest_receipt_no || "—"}
                </Typography>
              </Box>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Receipt numbers are never reused, even when payments are deleted. After clearing test
              payments, reset so numbering continues from the highest receipt still stored
              {info.highest_receipt_no === 0 ? " — with no payments left, the next receipt will be 1" : ""}.
            </Typography>

            <Button
              variant="outlined"
              color="warning"
              disabled={!canReset || resetting}
              onClick={() => setConfirmOpen(true)}
              sx={{ borderRadius: 999, px: 3 }}
            >
              {resetting
                ? "Resetting..."
                : canReset
                  ? `Reset — next receipt will be ${info.highest_receipt_no + 1}`
                  : "Numbering is already continuous"}
            </Button>
          </>
        )}
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Reset receipt numbering?</DialogTitle>
        <DialogContent>
          <Typography>
            The next receipt will be <b>No. {info ? info.highest_receipt_no + 1 : ""}</b> instead of{" "}
            <b>No. {info?.next_receipt_no}</b>. Only do this if the skipped numbers were never given
            to parents (for example, test payments you deleted).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={reset}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
